import type { ReactElement, SyntheticEvent } from "react"
import { memo, useContext, useEffect, useMemo } from "react"
import PageHeaderOrg from "@onestore/hel/dist/components/organisms/PageHeaderOrg"
import type Theme from "@onestore/hel/dist/typings/theme"
import _t from "@core/i18n"
import Media from "@gatsby-plugin-generic-page/components/Media"
import type { Link } from "@gatsby-plugin-onestore/fragments/link"
import useNavigation from "@gatsby-plugin-onestore/hooks/useNavigation"
import { HeaderContext } from "~/context/HeaderContext"
import useAccount from "~/hooks/useAccount"
import useBasket from "~/hooks/useBasket"
import useSiteMetadataQuery from "~/hooks/useSiteMetadataQuery"
import { useTogglePriceType } from "~/hooks/useTogglePriceType"
import {
  sendGaEvent,
  sendMenuViewEvent,
  sendToplayerShowEvent,
  userLogoutEvent,
  userMenuDropdownEvent,
} from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import { getLinkPropForUrl } from "~/lib/url"
import useMenuQuery from "../../hooks/useMenuQuery"
import useCart from "./useCart"

/**
 * Elements that keep the sticky page-header bar hidden while they occupy the
 * top of the viewport: UDS comparison tables (their own sticky head sits there)
 * and any section opting in via `data-suppresses-sticky-header`, such as the
 * full-video slider while its video covers the screen.
 */
const STICKY_BAR_BLOCKERS = ".uds-table, [data-suppresses-sticky-header]"

type HeaderProps = {
  disableCartClick?: boolean
  isDarkMode?: boolean
  hasHideBoxShadow?: boolean
  hasPriceTypeSwitcher?: boolean
}

function Header({
  disableCartClick = false,
  isDarkMode = false,
  hasHideBoxShadow = false,
  hasPriceTypeSwitcher = false,
}: HeaderProps): ReactElement<HeaderProps> {
  const { menu } = useMenuQuery()
  const cart = useCart(disableCartClick, menu.flatData.gaCartEvent)
  const {
    headerData: { logo },
  } = useSiteMetadataQuery()
  const logoDarkMode = isDarkMode ? menu.flatData.logoDarkMode : undefined

  const { account, logout } = useAccount()
  const { getBasket } = useBasket()

  const context = useContext(HeaderContext)

  const priceTypeSwitcherProps = useTogglePriceType()

  const { setHeaderHeight, setHeaderInitHeight } = context

  const onHeaderLoad = (height: number) => {
    setHeaderInitHeight(height)
  }

  useEffect(() => {
    const header = document.querySelector<HTMLElement>('header[role="banner"]')
    if (!header) return
    const stickyContent = header.firstElementChild as HTMLElement | null
    if (!stickyContent) return

    const readHeight = () => {
      const rect = stickyContent.getBoundingClientRect()
      setHeaderHeight(Math.max(0, rect.bottom))
    }

    const isAnyStickyBarBlockerAtTop = () => {
      const blockers =
        document.querySelectorAll<HTMLElement>(STICKY_BAR_BLOCKERS)
      for (let i = 0; i < blockers.length; i++) {
        const rect = blockers[i].getBoundingClientRect()
        if (rect.top < 0 && rect.bottom > 0) return true
      }
      return false
    }

    const updateHiddenByStickyBarBlocker = () => {
      // PageHeaderOrg shows its sticky bar by applying translateY(100%) via CSS.
      // Reset to translateY(0) while a blocker (a comparison table, or a
      // full-screen video slider) occupies the top position so both sticky
      // elements don't overlap.
      // PageHeaderOrg controls transform via CSS transitions only, not inline style,
      // so patching style.transform directly here is safe.
      stickyContent.style.transform = isAnyStickyBarBlockerAtTop()
        ? "translateY(0)"
        : ""
    }

    readHeight()
    updateHiddenByStickyBarBlocker()

    let rafId: number | undefined

    const handleScroll = () => {
      if (rafId !== undefined) return
      rafId = requestAnimationFrame(() => {
        updateHiddenByStickyBarBlocker()
        readHeight()
        rafId = undefined
      })
    }

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName !== "transform") return
      readHeight()
    }

    // A blocker can toggle its attribute without any scrolling — the full-video
    // slider sets it part-way through an animation, by which point the reader
    // may have stopped. Watching for that keeps the sticky bar in sync instead
    // of leaving it stranded on screen.
    //
    // Deliberately the whole document: a blocker starts out *without* the
    // attribute and gains it later, so there is no element to attach to up
    // front. The single-attribute filter keeps this cheap on the pages — most
    // of them — that never set it.
    const blockerObserver = new MutationObserver(handleScroll)

    blockerObserver.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ["data-suppresses-sticky-header"],
    })

    stickyContent.addEventListener("transitionend", handleTransitionEnd)
    window.addEventListener("scroll", handleScroll, { passive: true })

    return () => {
      blockerObserver.disconnect()
      stickyContent.removeEventListener("transitionend", handleTransitionEnd)
      window.removeEventListener("scroll", handleScroll)
      if (rafId !== undefined) cancelAnimationFrame(rafId)
    }
  }, [setHeaderHeight])

  const handleLogoutClick = (event: SyntheticEvent): void => {
    event.preventDefault()
    logout(getBasket)
    userLogoutEvent()
  }

  const barLinks = useMemo(
    () =>
      menu.flatData.links
        .filter((link) => link.flatData.showOutsideUserDropdownOnDesktop)
        .map((link: Link) => {
          const linkProps = getLinkPropForUrl(link.flatData.url)
          const { title, gaEvent } = link.flatData

          return {
            text: title,
            title,
            openInNewTab: linkProps?.href !== undefined,
            onClick: () => sendGaEvent(gaEvent),
            ...linkProps,
          }
        }),
    [menu.flatData.links]
  )

  const dropdownItems = useMemo(
    () =>
      menu.flatData.links.map((link: Link) => {
        const linkProps = getLinkPropForUrl(link.flatData.url)
        const {
          title,
          icon,
          hiddenOnMobile,
          showOutsideUserDropdownOnDesktop,
          gaEvent,
        } = link.flatData

        return {
          text: title,
          title,
          icon: !isEmpty(icon) ? <Media media={icon} /> : undefined,
          openInNewTab: linkProps?.href !== undefined,
          hiddenOnMobile,
          visibleInMobileMenuFooter: showOutsideUserDropdownOnDesktop,
          onClick: () => sendGaEvent(gaEvent),
          ...linkProps,
        }
      }),
    [menu.flatData.links]
  )

  const navigation = useNavigation(menu.flatData.navigation)

  const accountDropdownData = {
    buttonData: {
      label: _t("header.myAccount"),
      backgroundColor: "white" as Theme.Color,
    },
    dropdownItems: dropdownItems,
    userData: account
      ? {
          userName:
            account.type === "person"
              ? `${account.first_name} ${account.last_name}`
              : account.company_name || "",
          userId: `${_t("account_id")} ${account.id}`,
          logoutButtonText: _t("logout"),
          onLogout: handleLogoutClick,
        }
      : undefined,
    headerCloseButtonTitle: _t("button.close"),
  }

  return (
    <PageHeaderOrg
      barLinks={barLinks}
      accountDropdown={accountDropdownData}
      logo={{
        title: menu.flatData.siteName,
        src: !isEmpty(logoDarkMode)
          ? logoDarkMode[0].url
          : menu.flatData.logo[0].url,
        alt: `Logo ${menu.flatData.siteName}`,
        width:
          !isEmpty(logoDarkMode) && typeof logoDarkMode[0].width === "number"
            ? logoDarkMode[0].width
            : logo.width,
        height:
          !isEmpty(logoDarkMode) && typeof logoDarkMode[0].height === "number"
            ? logoDarkMode[0].height
            : logo.height,
        to: "/",
        onClick: () => sendGaEvent(menu.flatData.gaLogoEvent),
      }}
      navigation={navigation}
      cart={cart}
      mobileNavigationBackLinkText={menu.flatData.mobileNavigationBackLinkText}
      onHeaderLoad={onHeaderLoad}
      hamburgerOnClick={() => {
        sendMenuViewEvent()
        window.jentisDataLayer?.pushMenuViewEvent()

        sendToplayerShowEvent()
        window.jentisDataLayer?.pushToplayerShowEvent()
      }}
      isDarkMode={isDarkMode}
      hasHideBoxShadow={hasHideBoxShadow}
      netGrossSwitcher={
        hasPriceTypeSwitcher ? priceTypeSwitcherProps : undefined
      }
      netGrossSwitcherMobileText={_t("priceTypeToggleLabel")}
      accountOnClick={userMenuDropdownEvent}
    />
  )
}

export default memo(Header)
